// eslint-disable-next-line @typescript-eslint/no-explicit-any

import { DOCUMENT } from "@angular/common";
import { inject, Injectable, InjectionToken, NgZone, OnDestroy, Provider } from "@angular/core";
import { Rectangle, Scene, Viewer } from "cesium";
import { DEFAULT_CESIUM_VIEWER_CONFIGURATION_OPTIONS } from "..";

declare global {
    interface Window {
        CESIUM_BASE_URL?: string;
    }
}

export const CESIUM_VIEWER_CONFIGURATION_OPTIONS = new InjectionToken<Partial<Viewer.ConstructorOptions>>(
    "CESIUM_VIEWER_CONFIGURATION_OPTIONS",
    { factory: () => DEFAULT_CESIUM_VIEWER_CONFIGURATION_OPTIONS }
);

const CESIUM_BASE_URL = "/assets/cesium";

@Injectable()
export class CesiumMapService implements OnDestroy {
    private readonly document = inject(DOCUMENT);
    private readonly windowRef = this.document.defaultView;
    private readonly cesiumViewerConfigurationOptions = inject(CESIUM_VIEWER_CONFIGURATION_OPTIONS);
    private readonly ngZone = inject(NgZone);
    private viewer!: Viewer;
    private mapContainer!: HTMLDivElement;
    private resizeObserver: ResizeObserver | undefined;

    constructor() {
        this.ngZone.runOutsideAngular(() => {
            if (!this.windowRef) {
                throw new Error("Window reference is not available.");
            }

            this.windowRef.CESIUM_BASE_URL = CESIUM_BASE_URL;
            this.mapContainer = this.document.createElement("div");
            this.mapContainer.style.width = "100%";
            this.mapContainer.style.height = "100%";
            this.viewer = new Viewer(this.mapContainer, this.cesiumViewerConfigurationOptions);
            this.viewer.scene.postProcessStages.fxaa.enabled = true;

            // NOTE: Optimizing Cesium SceneTransforms.wgs84ToDrawingBufferCoordinates
            // by monkey-patching canvas.clientWidth and canvas.clientHeight
            const canvas = this.viewer.canvas;
            let cachedWidth = canvas.width;
            let cachedHeight = canvas.height;

            this.resizeObserver = new ResizeObserver((entries) => {
                cachedWidth = entries[0].contentRect.width;
                cachedHeight = entries[0].contentRect.height;
            });

            this.resizeObserver.observe(this.mapContainer);

            Object.defineProperty(canvas, "clientWidth", { get: () => cachedWidth });
            Object.defineProperty(canvas, "clientHeight", { get: () => cachedHeight });
        });
    }

    public ngOnDestroy(): void {
        // NOTE: setTimeout is used to make sure Viewer is destroyed after Angular has finished components destruction
        setTimeout(() => {
            this.viewer.destroy();
            this.mapContainer.remove();
        });

        this.resizeObserver?.disconnect();
    }

    public getScene(): Scene {
        return this.viewer.scene;
    }

    public getViewer(): Viewer {
        return this.viewer;
    }

    public getMapContainer() {
        return this.mapContainer;
    }

    public static isViewRectangleValid(rectangle: Rectangle): boolean {
        return isFinite(rectangle.west) && isFinite(rectangle.east) && isFinite(rectangle.south) && isFinite(rectangle.north);
    }

    public static getUpdatedConfigurationOptionsProvider(options: Partial<Viewer.ConstructorOptions>): Provider {
        return {
            provide: CESIUM_VIEWER_CONFIGURATION_OPTIONS,
            useValue: { ...DEFAULT_CESIUM_VIEWER_CONFIGURATION_OPTIONS, ...options },
        };
    }
}
